import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Home, Store, Bell, ShoppingBasket, ArrowRight,
  Zap, Plus,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { isWhiteLabel } from '../config/tenantConfig';
import brokerService from '../services/brokerService';
import scheduleService from '../services/scheduleService';
import basketService from '../services/basketService';
import { scheduleToPositionRow } from '../utils/scheduleToPositionRow';
import marketplaceService from '../services/marketplaceService';
import allocationService from '../services/allocationService';
import { MarketplaceSubscription, MarketplaceListing } from '../types/marketplace';
import type { BrokerAccount, PositionRow, Basket, BasketBrokerAllocation } from '../types';
import { ALLOCATION_STATUS } from '../constants/tradingConstants';
import { isMarketOpen, getMarketStatusText, getTodayIST } from '../utils/marketHours';
import { getHolidays } from '../services/referenceDataService';

import SubscriptionCard from '../components/marketplace/SubscriptionCard';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/ui/Button';

import TodayPnlCards from '../components/dashboard/TodayPnlCards';
import BrokerHealthCards from '../components/dashboard/BrokerHealthCards';
import OpenPositionsCard from '../components/dashboard/OpenPositionsCard';
import RecentTradesCard from '../components/dashboard/RecentTradesCard';
import BasketPnlChart from '../components/dashboard/BasketPnlChart';

const REFRESH_INTERVAL_MS = 60_000; // 60 seconds during market hours

const Dashboard: React.FC = () => {
  const { user, isAdmin } = useAuth() as any;
  const whiteLabel = isWhiteLabel();

  // Shared state
  const [brokerAccounts, setBrokerAccounts] = useState<BrokerAccount[]>([]);
  const [positions, setPositions] = useState<PositionRow[]>([]);
  const [baskets, setBaskets] = useState<Basket[]>([]);
  const [loading, setLoading] = useState(true);
  const [holidays, setHolidays] = useState<Record<string, string>>({});
  // Tracks whether the holiday list has settled (resolved OR failed). Until
  // then the status shows a neutral "checking" state rather than risk a brief
  // wrong "Market Open" flash on a holiday before the list loads.
  const [holidaysReady, setHolidaysReady] = useState(false);
  // Bumped every minute to re-derive the market status as the clock advances.
  const [, setMinuteTick] = useState(0);

  // White-label state
  const [subscriptions, setSubscriptions] = useState<MarketplaceSubscription[]>([]);
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [allocations, setAllocations] = useState<BasketBrokerAllocation[]>([]);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load trading holidays once so the market-status banner can say "Market
  // Holiday — <name>" instead of falsely showing the market as open today.
  // Cached (1h TTL) by referenceDataService; failure is non-fatal — we fall
  // back to weekend-only status. `holidaysReady` flips on settle either way.
  useEffect(() => {
    getHolidays()
      .then((r) => setHolidays(r.holidays || {}))
      .catch(() => { /* non-fatal — weekend-only status */ })
      .finally(() => setHolidaysReady(true));
  }, []);

  // Minute ticker — forces the derived market status (computed during render
  // below) to recompute as the clock advances.
  useEffect(() => {
    const timer = setInterval(() => setMinuteTick((n) => n + 1), 60_000);
    return () => clearInterval(timer);
  }, []);

  // Dashboard positions feed comes from schedule_viewer's format=positions
  // endpoint (default scope = today's date-scoped, broker-aligned). Rows are
  // adapted via scheduleToPositionRow so the Dashboard P&L / Open / Recent
  // cards keep their existing PositionRow contract.
  const fetchTradingData = useCallback(async () => {
    const today = getTodayIST();
    try {
      const posRes = await scheduleService.getPositions(today, 'default')
        .catch(() => ({ positions: [] as any[] }));
      setPositions((posRes.positions || []).map(scheduleToPositionRow));
    } catch {
      // Errors already caught per-call
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const brokerPromise = brokerService.getBrokerAccounts().catch(() => [] as BrokerAccount[]);

      if (whiteLabel) {
        const [accounts, subs, strats, posRes, basketRes, allocs] = await Promise.all([
          brokerPromise,
          marketplaceService.getSubscriptions().catch(() => [] as MarketplaceSubscription[]),
          marketplaceService.getListings().catch(() => [] as MarketplaceListing[]),
          scheduleService.getPositions(getTodayIST(), 'default').catch(() => ({ positions: [] as any[] })),
          basketService.getBaskets().catch(() => [] as Basket[]),
          allocationService.getAllAllocations().catch(() => [] as BasketBrokerAllocation[]),
        ]);
        setBrokerAccounts(accounts);
        setSubscriptions(subs);
        setListings(strats);
        setPositions((posRes.positions || []).map(scheduleToPositionRow));
        setBaskets(basketRes);
        setAllocations(allocs);
      } else {
        const [accounts, posRes, basketRes] = await Promise.all([
          brokerPromise,
          scheduleService.getPositions(getTodayIST(), 'default').catch(() => ({ positions: [] as any[] })),
          basketService.getBaskets().catch(() => [] as Basket[]),
        ]);
        setBrokerAccounts(accounts);
        setPositions((posRes.positions || []).map(scheduleToPositionRow));
        setBaskets(basketRes);
      }
    } finally {
      setLoading(false);
    }
  }, [whiteLabel]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Auto-refresh positions & orders during market hours
  useEffect(() => {
    const setupRefresh = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (holidaysReady && isMarketOpen(holidays)) {
        intervalRef.current = setInterval(fetchTradingData, REFRESH_INTERVAL_MS);
      }
    };

    setupRefresh();
    // Re-check every minute whether market is open
    const checker = setInterval(setupRefresh, 60_000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      clearInterval(checker);
    };
  }, [fetchTradingData, holidays, holidaysReady]);

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const firstName = user?.fullName?.split(' ')[0] || 'User';
  // Derived during render (not stored in state) so the displayed status never
  // lags the holidays load by a frame. Until the list settles we show a
  // neutral "checking" pill instead of a possibly-wrong open/closed state.
  const marketOpen = holidaysReady && isMarketOpen(holidays);
  const marketStatusKind: 'active' | 'inactive' | 'loading' =
    !holidaysReady ? 'loading' : marketOpen ? 'active' : 'inactive';
  const marketStatusText = holidaysReady
    ? getMarketStatusText(holidays)
    : 'Checking market status…';

  // ========== WHITE-LABEL DASHBOARD ==========
  if (whiteLabel) {
    const activeSubs = subscriptions.filter(s => s.status === 'ACTIVE');
    const pausedSubs = subscriptions.filter(s => s.status === 'PAUSED');
    const visibleSubs = [...activeSubs, ...pausedSubs].slice(0, 3);

    // Custom baskets metrics
    const activeAllocatedBasketIds = new Set(
      allocations.filter(a => a.status === ALLOCATION_STATUS.ACTIVE).map(a => a.basket_id)
    );
    const totalBaskets = baskets.length;
    const activeOnBrokerCount = activeAllocatedBasketIds.size;

    const showCustomBaskets = isAdmin || baskets.length > 0;

    return (
      <StandardLayout>
        {/* Hero band */}
        <div className="bg-gradient-to-br from-zebu-50 via-white to-white dark:from-zebu-900/10 dark:via-gray-800 dark:to-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-5 py-4">
          <PageHeader
            title={`${getWelcomeMessage()}, ${firstName}!`}
            pageType="dashboard"
            status={marketStatusKind}
            statusText={marketStatusText}
            breadcrumbs={[{ label: 'Home', icon: <Home className="w-4 h-4" /> }]}
          />
        </div>

        {/* Today's P&L + Orders */}
        <TodayPnlCards positions={positions} loading={loading} />

        {/* Marketplace group */}
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-2 mb-2">
            <Store className="w-3.5 h-3.5" />
            Marketplace
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="bg-zebu-50/60 dark:bg-zebu-900/10 rounded-lg border border-zebu-200 dark:border-zebu-800/60 p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-zebu-700 dark:text-zebu-300 uppercase tracking-wider">Active Subscriptions</p>
                  <p className="text-xl font-bold text-zebu-600 dark:text-zebu-400 mt-1 tabular-nums">{loading ? '\u2014' : activeSubs.length}</p>
                </div>
                <Bell className="w-5 h-5 text-zebu-500 dark:text-zebu-400" strokeWidth={1.75} />
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Baskets Running</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white mt-1 tabular-nums">{loading ? '\u2014' : activeSubs.length}</p>
                </div>
                <Zap className="w-5 h-5 text-zebu-500/70 dark:text-zebu-400/70" strokeWidth={1.75} />
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Available Baskets</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white mt-1 tabular-nums">{loading ? '\u2014' : listings.length}</p>
                </div>
                <Store className="w-5 h-5 text-zebu-500/70 dark:text-zebu-400/70" strokeWidth={1.75} />
              </div>
            </div>
          </div>
        </div>

        {/* Custom Baskets group — hidden for non-admin users with no baskets */}
        {showCustomBaskets && (
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-2 mb-2">
              <ShoppingBasket className="w-3.5 h-3.5" />
              Custom Baskets
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Baskets</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white mt-1 tabular-nums">{loading ? '\u2014' : totalBaskets}</p>
                  </div>
                  <ShoppingBasket className="w-5 h-5 text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg border border-gray-300 dark:border-gray-600 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">Active on Broker</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white mt-1 tabular-nums">{loading ? '\u2014' : activeOnBrokerCount}</p>
                  </div>
                  <Zap className="w-5 h-5 text-gray-500 dark:text-gray-400" strokeWidth={1.75} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Active Subscriptions */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Active Subscriptions</h2>
            {subscriptions.length > 0 && (
              <Link to="/subscriptions" className="flex items-center gap-1 text-sm font-medium text-zebu-600 dark:text-zebu-400 hover:text-zebu-700">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
          {subscriptions.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {visibleSubs.map(sub => (
                <SubscriptionCard key={sub.subscription_id} subscription={sub} onUpdate={fetchAllData} />
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-6 flex flex-col sm:flex-row items-center sm:items-start sm:justify-between gap-4 text-center sm:text-left">
              <div className="flex items-start gap-3">
                <div className="bg-zebu-50 dark:bg-zebu-900/20 p-2 rounded-lg shrink-0">
                  <Store className="w-5 h-5 text-zebu-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">No subscriptions yet</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Browse the marketplace to discover expert-built algo strategies.</p>
                </div>
              </div>
              <Link
                to="/marketplace"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md bg-zebu-600 hover:bg-zebu-700 text-white transition-colors shrink-0"
              >
                Browse Marketplace
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/marketplace"
            className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md hover:-translate-y-0.5 transition-all group"
          >
            {/* Decorative backdrop icon */}
            <Store className="absolute -right-4 -bottom-4 w-28 h-28 text-zebu-100 dark:text-zebu-900/20" strokeWidth={1} />
            <div className="relative">
              <div className="bg-zebu-50 dark:bg-zebu-900/20 w-11 h-11 rounded-lg flex items-center justify-center mb-4">
                <Store className="w-5 h-5 text-zebu-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Browse Marketplace</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Discover expert-built algo strategies and start trading in minutes.</p>
              <span className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-zebu-600 dark:text-zebu-400 group-hover:gap-2 transition-all">
                Explore <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
          <Link
            to="/baskets"
            className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md hover:-translate-y-0.5 transition-all group"
          >
            {/* Decorative backdrop icon */}
            <ShoppingBasket className="absolute -right-4 -bottom-4 w-28 h-28 text-zebu-100 dark:text-zebu-900/20" strokeWidth={1} />
            <div className="relative">
              <div className="bg-zebu-50 dark:bg-zebu-900/20 w-11 h-11 rounded-lg flex items-center justify-center mb-4">
                <ShoppingBasket className="w-5 h-5 text-zebu-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Create Basket</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Build your own custom baskets with the strategies you trade.
                {isAdmin && ' Publish them to the marketplace when ready.'}
              </p>
              <span className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-zebu-600 dark:text-zebu-400 group-hover:gap-2 transition-all">
                Create <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        </div>
      </StandardLayout>
    );
  }

  // ========== QUANTLEAP DASHBOARD ==========
  return (
    <StandardLayout>
      <PageHeader
        title={`${getWelcomeMessage()}, ${firstName}!`}
        pageType="dashboard"
        status={marketStatusKind}
        statusText={marketStatusText}
        breadcrumbs={[{ label: 'Home', icon: <Home className="w-4 h-4" /> }]}
        actions={
          <Button
            onClick={() => window.location.href = '/brokers'}
            leftIcon={<Plus className="h-4 w-4" />}
            className="w-full sm:w-auto"
          >
            Connect Broker
          </Button>
        }
      />

      {/* Account Summary Strip */}
      <TodayPnlCards positions={positions} loading={loading} />

      {/* Broker Status */}
      <BrokerHealthCards accounts={brokerAccounts} loading={loading} />

      {/* Open Positions + Recent Trades */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <OpenPositionsCard positions={positions} loading={loading} />
        <RecentTradesCard positions={positions} loading={loading} />
      </div>

      {/* Basket P&L */}
      {baskets.length > 0 && <BasketPnlChart baskets={baskets} positions={positions} />}
    </StandardLayout>
  );
};

export default Dashboard;
